import { Injectable } from '@angular/core';

import { mergeMap, withLatestFrom } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Router } from '@angular/router';
import { CancelledDeviceTaskService, DeviceTaskService } from '@era/device-task-management-sdk';
import { Store } from '@ngrx/store';
import { safeMap } from '../../../../stores/effects.helpers';
import { DevicesRouterStoreSelectors } from '../../../../stores/router';
import {
  DeviceScheduledTasksResolverActions,
  DeviceScheduledTasksApiActions,
  DeviceScheduledTasksUiActions,
  SignalRActions,
} from './device-scheduled-tasks.actions';

@Injectable()
export class DeviceScheduledTasksEffects {
  constructor(
    private readonly actions: Actions,
    private readonly deviceTaskService: DeviceTaskService,
    private readonly cancelledDeviceTaskService: CancelledDeviceTaskService,
    private readonly routerStore: Store<Router>
  ) {}

  scheduledTasksRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeviceScheduledTasksResolverActions.scheduledTasksRequested),
      mergeMap(({ udid }) =>
        safeMap(
          this.deviceTaskService.getDeviceTasks(udid),
          (scheduledTasks) => DeviceScheduledTasksApiActions.scheduledTasksLoaded({ scheduledTasks }),
          (error) =>
            DeviceScheduledTasksApiActions.scheduledTasksLoadedError({
              error,
            })
        )
      )
    );
  });

  scheduledTaskDeletedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeviceScheduledTasksUiActions.scheduledTaskDeleted),
      mergeMap(({ deviceTask }) =>
        safeMap(
          this.cancelledDeviceTaskService.cancel(deviceTask.messageId, deviceTask.udid, deviceTask.task),
          () => DeviceScheduledTasksApiActions.scheduledTaskDeleted({ deviceTask }),
          (error) =>
            DeviceScheduledTasksApiActions.scheduledTaskDeletedError({
              error,
            })
        )
      )
    );
  });

  scheduledTasksDeletedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeviceScheduledTasksUiActions.scheduledTasksDeleted),
      withLatestFrom(this.routerStore.select(DevicesRouterStoreSelectors.selectUdid)),
      mergeMap(([, udid]) =>
        safeMap(
          this.deviceTaskService.cancelDeviceTasks(udid),
          () => DeviceScheduledTasksApiActions.scheduledTasksDeleted({ udid }),
          (error) =>
            DeviceScheduledTasksApiActions.scheduledTasksDeletedError({
              error,
            })
        )
      )
    );
  });

  scheduleTaskEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeviceScheduledTasksUiActions.taskScheduled),
      withLatestFrom(this.routerStore.select(DevicesRouterStoreSelectors.selectUdid)),
      mergeMap(([{ taskType }, udid]) =>
        safeMap(
          this.deviceTaskService.startSync(udid, taskType),
          () => DeviceScheduledTasksApiActions.taskScheduled({ udid, taskType }),
          (error) =>
            DeviceScheduledTasksApiActions.taskScheduledError({
              error,
            })
        )
      )
    );
  });

  signalRScheduledTasksUpdateEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SignalRActions.scheduledTasksUpdated),
      mergeMap(({ udid }) =>
        safeMap(
          this.deviceTaskService.getDeviceTasks(udid),
          (scheduledTasks) => DeviceScheduledTasksApiActions.scheduledTasksLoaded({ scheduledTasks }),
          (error) =>
            DeviceScheduledTasksApiActions.scheduledTasksLoadedError({
              error,
            })
        )
      )
    );
  });
}
